
import { defineComponent, PropType, reactive, onMounted, computed } from 'vue'

import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

import useNotifications from '@/common/composable/useNotifications'

import { D24_TYPES } from '@/core/constants/contracts.constants'

import { contracts } from '@/core/realisations/contracts.realisation'

type Signer = {
    email: string
    isSigned: boolean
    isLawyer: boolean
    typeId: number
}

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        signers: {
            type: Array as PropType<Signer[]>,
            default: () => []
        }
    },
    emits: ['cancel', 'confirm'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            isWhitelistLoading: false,
            isWhitelistFailed: false,
            whitelist: [] as { id: string; value: string; }[],
            //
            signers: [] as Signer[],
            signersValidation: [] as string[]
        })

        const onCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        const loadWhitelist = async () => {
            try {
                state.isWhitelistLoading = true

                const response = await contracts.getConfigValueByKey('dogovor24_allowed_signer_emails')

                state.whitelist = response.data.value.map((s) => {
                    return {
                        id: s,
                        value: s
                    }
                })

                state.isWhitelistFailed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })

                state.isWhitelistFailed = true
            } finally {
                state.isWhitelistLoading = false
            }
        }

        onMounted(() => {
            loadWhitelist()

            state.signers = props.signers.map(s => ({ ...s }))
            state.signersValidation = Array.from({ length: props.signers.length }, () => '')
        })

        const onUpdateWhitelistedSigner = (s: string, i: number) => {
            state.signers[i].email = s
        }

        const onUpdateSigner = (v: string, i: number) => {
            state.signers[i].email = v
            validateModalItems()
        }

        const isValidEmail = (email: string): boolean => {
            const trimmed = email.trim()
            if (!trimmed) return false

            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return re.test(trimmed)
        }

        const validateValue = (s: Signer, i: number) => {
            if (!Boolean(s.email)) {
                state.signersValidation[i] = 'Обязательное поле'
            } else if (!isValidEmail(s.email)) {
                state.signersValidation[i] = 'Невалидный email'
            } else {
                state.signersValidation[i] = ''
            }
        }

        const validateModalItems = () => {
            state.signers.forEach((s, i) => validateValue(s, i))
        }

        const onConfirm = () => {
            if (props.loading || isModalInvalid.value) return

            if (!state.signers.every((signer) => signer.email && signer.typeId)) {
                notifications.notify({ type: 'danger', title: 'Заполните все необходимые поля!' })
                return
            }

            const signersData = props.signers.map((signer, i) => {
                return {
                    email: signer.email,
                    newEmail: state.signers.length ? state.signers[i].email : null,
                    dogovor24TypeId: signer.typeId,
                }
            }).filter((signer) => signer.email !== signer.newEmail)

            if (signersData.length === 0) {
                notifications.notify({ type: 'danger', title: 'Вы не внесли изменения' })
                return
            }

            emit('confirm', signersData)
        }

        const onChangeType = (i: number) => {
            state.signers[i].email = ''
        }

        const isModalInvalid = computed(() => state.signersValidation.filter(Boolean).length > 0)

        return {
            D24_TYPES,
            state,
            onCancel,
            loadWhitelist,
            onUpdateWhitelistedSigner,
            onUpdateSigner,
            onConfirm,
            onChangeType,
            isModalInvalid
        }
    }
})
